/* eslint-disable no-underscore-dangle */
import { nanoid } from 'nanoid';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import { dropAt } from '../../../../utils';
import Button from '../../../generic/Button';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import { icons, IconText } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import useLanguageSettings from '../useLanguageSettings';
import type { PubFetcherConfig, PubServer } from '../../../../../typings/common';

import Server from './Server';

/**
 * Pub settings page.
 */
const PubSettings: VoidFunctionComponent = () => {
  const {
    settings,
    isSaving,
    onUpdateSettings,
    onSaveSettings,
    isLoading,
    isSaveButtonDisabled,
  } = useLanguageSettings(
    window.user?.organizationId as number,
    'pub',
  );

  const servers = settings?.servers || [];

  const onAdd = (configKey: keyof PubFetcherConfig) => {
    onUpdateSettings({
      [configKey]: [...(settings?.[configKey] || []), { _id: nanoid() }],
    });
  };

  const onRemove = (configKey: keyof PubFetcherConfig, index: number) => {
    const configSourceValues: PubFetcherConfig[typeof configKey] = settings?.[configKey] || [];
    onUpdateSettings({
      [configKey]: dropAt(configSourceValues, index),
    });
  };

  const onUpdateServer = <K extends keyof PubServer>(
    index: number,
    member: K,
    value: PubServer[K],
  ) => {
    onUpdateSettings({
      servers: servers.map((server, i) => {
        if (i === index) {
          return {
            ...server,
            [member]: value,
            hasToken: member === 'token' ? false : server.hasToken,
          };
        }
        return server;
      }),
    });
  };

  const isFreeOrg = isOrgInFreeTier();

  return (
    <PaidFeatureGate isRestricted={isFreeOrg} hidePill>
      <Header heading="Pub Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled}
          isLoading={isSaving}
        />
      </Header>
      {isLoading
        ? <Loader />
        : (
          <Section>
            <Section.Left heading="Servers">
              <Section.Paragraph>
                Configure Pub server settings for your organization.
              </Section.Paragraph>
            </Section.Left>
            <Section.Right>
              {servers.map((server, index) =>
                <Server
                  key={server._id}
                  id={server.id}
                  token={server.token}
                  url={server.url}
                  hasToken={server.hasToken}
                  onRemove={() => onRemove('servers', index)}
                  onUpdateServer={
                    <K extends keyof PubServer>(
                      member: K,
                      value: PubServer[K],
                    ) => onUpdateServer(index, member, value)
                  }
                />,
              )}
              <Button
                type="secondary"
                size="medium"
                onClick={() => onAdd('servers')}
              >
                <IconText icon={icons.plus} text="Add" />
              </Button>
            </Section.Right>
          </Section>
          )}
    </PaidFeatureGate>
  );
};

export default PubSettings;
